import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useCart, itemLine } from "@/lib/cart";
import { suggestionsFor, combosFor, comboDiscount, discountText } from "@/lib/combos";
import { baseMessage, waLink, formatPrice } from "@/lib/store";
import { AccessoryPicker, forModelOf } from "./AccessoryPicker";
import { WhatsAppIcon } from "./WhatsAppIcon";

const buildMessage = (p, picked) => {
  if (!picked.length) return baseMessage(p);
  const entries = picked.map((a) => ({ product: a, qty: 1, forModel: forModelOf(a, p.model) }));
  const sum = entries.reduce((s, e) => s + e.product.price, 0);
  const d = comboDiscount(entries);
  return [
    baseMessage(p).replace(/ Ainda está disponível\?$/, ""),
    "",
    "Também quero levar:",
    ...entries.map(itemLine),
    ...discountText(d),
    `*Total dos acessórios: ${formatPrice(sum - d.total)}*`,
    "",
    "Ainda está disponível?",
  ].join("\n");
};

export const InterestDialog = () => {
  const { interestFor: p, setInterestFor, catalog } = useCart();
  const [picked, setPicked] = useState([]);
  const list = useMemo(() => (p && catalog ? suggestionsFor(catalog, p.model) : []), [catalog, p]);
  const combos = useMemo(() => combosFor(list), [list]);

  useEffect(() => { setPicked([]); }, [p?.id]);

  const isOn = (a) => picked.some((x) => x.id === a.id);
  const toggle = (a) => setPicked((s) => (s.some((x) => x.id === a.id) ? s.filter((x) => x.id !== a.id) : [...s, a]));
  const toggleCombo = (c) => setPicked((s) => {
    const all = c.items.every((a) => s.some((x) => x.id === a.id));
    const rest = s.filter((x) => !c.items.some((a) => a.id === x.id));
    return all ? rest : [...rest, ...c.items];
  });
  const close = () => setInterestFor(null);
  const d = comboDiscount(picked.map((a) => ({ product: a, qty: 1 })));
  const extra = picked.reduce((s, a) => s + a.price, 0) - d.total;

  return (
    <Dialog open={!!p} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="interest-dialog" className="max-h-[92vh] w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-[28px] border-white/10 bg-[#0d0d0f] p-0 text-white">
        <div className="px-6 pb-4 pt-6">
          <DialogTitle className="font-display text-2xl font-bold text-white">Quer levar algum acessório?</DialogTitle>
          <DialogDescription className="mt-1 text-sm text-zinc-400">Marque o que quiser e enviamos tudo junto com o {p?.model}. Combos com 10% off.</DialogDescription>
        </div>
        <AccessoryPicker list={list} combos={combos} isOn={isOn} onToggle={toggle} onCombo={toggleCombo} labels={["Quero", "Escolhido"]} />
        <div className="mt-4 space-y-2 border-t border-white/10 px-6 pb-6 pt-4">
          {picked.length > 0 && (
            <p data-testid="interest-extra" className="text-right text-sm text-zinc-300">
              + {formatPrice(extra)} em acessórios{d.total > 0 && <span className="text-emerald-300"> (economia de {formatPrice(d.total)})</span>}
            </p>
          )}
          {p && (
            <a data-testid="interest-send" data-pid={p?.id} data-pmodel={p?.model} href={waLink(buildMessage(p, picked))} target="_blank" rel="noopener noreferrer" onClick={close}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-5 py-3.5 text-sm font-bold text-black transition-transform active:scale-95">
              <WhatsAppIcon className="h-4 w-4" /> {picked.length ? `Enviar com ${picked.length} acessório${picked.length > 1 ? "s" : ""}` : "Não, só o iPhone"}
            </a>
          )}
          {p && picked.length > 0 && (
            <a data-testid="interest-only-iphone" data-pid={p?.id} data-pmodel={p?.model} href={waLink(baseMessage(p))} target="_blank" rel="noopener noreferrer" onClick={close}
              className="block w-full rounded-full border border-white/20 px-5 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Não, só o iPhone
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
